import { useDocumentReferencesForBinaryIds } from '@ctw/shared/api/fhir/binaries';
import { useEpisodeOfCareById } from '@ctw/shared/api/fhir/episode-of-care';
import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { Button } from '@ctw/shared/components/button';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList, type DefinitionListItem } from '@ctw/shared/components/definition-list';
import { Link } from '@ctw/shared/components/link';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import {
  EncounterRelatedDocumentsList,
  type EncounterRelatedDocumentsListItem,
} from '@ctw/shared/content/documents/encounter-related-documents-list';
import { PatientInfoLine } from '@ctw/shared/content/patients/patient-info-line';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import {
  type UseResourceDrawerOpenDrawerArgs,
  useResourceDrawer,
} from '@ctw/shared/context/resource-drawer-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compact, get, groupBy, isEmpty, mapValues } from 'lodash-es';

export const episodeOfCareDrawerAction: UseResourceDrawerOpenDrawerArgs<'EpisodeOfCare'>['action'] =
  ({ model, patient }) => ({
    label: 'Episode of Care',
    title: model.title,
    subtitle: model.subtitle,
    badges: compact([
      model.isReadmission && {
        variant: 'info',
        content: 'Readmission',
      },
      model.isNonelectiveAdmission && {
        variant: 'info',
        content: 'Nonelective',
      },
      model.isBehavioralHealth && {
        variant: 'info',
        content: 'Behavioral',
      },
    ]),
    content: {
      body: model.patientUPID && <EpisodeOfCareDrawerBody liteModel={model} patient={patient} />,
    },
  });

interface EpisodeOfCareDrawerBodyProps {
  liteModel: EpisodeOfCareModel;
  patient: PatientModel;
}

// Overview card opens the drawer with a "lite" model from the trimmed overview
// query, which omits nested Encounter data and resource.diagnosis. Refetch the
// full EoC here so sections like Encounters and Diagnoses render correctly.
// Falls back to the lite model while loading so the header/details/binaries
// remain visible immediately.
const EpisodeOfCareDrawerBody = ({ liteModel, patient }: EpisodeOfCareDrawerBodyProps) => {
  const fullQuery = useEpisodeOfCareById(liteModel.id);
  const model = fullQuery.data ?? liteModel;
  const patientUPID = model.patientUPID ?? '';

  return (
    <>
      <div className={tw`w-full py-2`}>
        {!window.location.pathname.includes(patientUPID) &&
          (patient || model.patient) &&
          (patient.patientUPID || patientUPID) && (
            <PatientHeader
              patient={model.patient ?? patient}
              patientUPID={patientUPID || patient.patientUPID || ''}
            />
          )}
        <PatientInfoLine patient={model.patient ?? patient} />
      </div>
      <div className={tw`flex flex-col gap-5`}>
        <ContentCard
          variant="focal"
          title="Details"
          content={{
            header: <ResponsiveSourceDocumentLink model={model} />,
          }}
        >
          <DefinitionList items={episodeOfCareData(model)} />
        </ContentCard>
        {fullQuery.isLoading ? (
          <LoadingSpinner centered={true} message="Loading encounters..." />
        ) : (
          <AggregatedEncounters key={model.id} model={model} patient={patient} />
        )}
        <RelatedBinaries model={model} />
      </div>
    </>
  );
};

const episodeOfCareData = (toc: EpisodeOfCareModel): Array<DefinitionListItem> => {
  const diagnosisGroupsByRole = mapValues(
    groupBy(toc.resource.diagnosis, 'role.coding[0].code'),
    (group, code) => ({
      display: get(group, '[0].role.coding[0].display', ''),
      diagnoses: toc.diagnosesForRole(code),
    }),
  );

  const diagnosisListItems = Object.entries(diagnosisGroupsByRole || {}).map(([_, group]) => ({
    label: group.display,
    value:
      group.diagnoses.length > 0 ? <DiagnosisDetails diagnosis={group.diagnoses} /> : undefined,
  }));

  return [
    { label: 'Start Date', value: toc.periodStart },
    { label: 'End Date', value: toc.periodEnd },
    { label: 'Type', value: toc.class },
    { label: 'Last Location', value: toc.lastLocation },
    ...diagnosisListItems,
    { label: 'Discharge Disposition', value: toc.dischargeDisposition },
  ].filter((entry) => !isEmpty(entry.value));
};

interface AggregatedEncountersProps {
  model: EpisodeOfCareModel;
  patient: PatientModel;
}

const AggregatedEncounters = ({ model, patient }: AggregatedEncountersProps) => {
  const { pushView } = useResourceDrawer();

  return (
    <div className={tw`space-y-4`}>
      <div className={tw`font-semibold text-lg`}>Encounters</div>
      {model.aggregatedEncounters.map((item) => (
        <div
          key={item.id}
          className={tw`flex flex-col gap-2 divide-y divide-divider-main rounded-lg border bg-white p-3 text-left`}
        >
          <div className={tw`flex items-start justify-between gap-2`}>
            <div className={tw`flex flex-col`}>
              <span
                className={tw`whitespace-normal break-words font-medium text-background-inverse text-base`}
              >
                {item.classDisplay}
              </span>
              <span
                className={tw`whitespace-normal break-words font-medium text-base text-content-subtle`}
              >
                {item.location.join(', ')}
              </span>
            </div>
            <div className={tw`flex shrink-0 items-center justify-end`}>
              <Button
                type="button"
                variant="link"
                onClick={() =>
                  pushView({
                    patient,
                    resourceType: item.resourceType,
                    resourceId: item.id,
                  })
                }
              >
                View Encounter
              </Button>
            </div>
          </div>
          <DefinitionList
            items={[
              {
                label: 'Discharge',
                value: item.dischargeDisposition,
              },
              {
                label: 'Providers',
                value: item.practitioners.join(', '),
              },
              {
                label: 'Specialties',
                value: item.specialties.join(', '),
              },
              {
                label: 'Diagnoses',
                value: item.diagnoses.join(', '),
              },
            ].filter((definitionListItem) => !isEmpty(definitionListItem.value))}
          />
        </div>
      ))}
    </div>
  );
};

interface RelatedBinariesProps {
  model: EpisodeOfCareModel;
}

const RelatedBinaries = ({ model }: RelatedBinariesProps) => {
  const hasShowSuspectedDuplicateDocumentsFeature = useFeatureFlag(
    'ctw-show-suspected-duplicate-documents',
  );
  const binaryIds = model.dischargeBinaryIds;

  const query = useDocumentReferencesForBinaryIds({
    upid: model.patientUPID ?? '',
    binaryIds,
    enabled: binaryIds.length > 0,
    excludeSuspectedDuplicates: !hasShowSuspectedDuplicateDocumentsFeature,
    includePlainText: true,
  });

  if (binaryIds.length === 0) {
    return <></>;
  }

  const items: Array<EncounterRelatedDocumentsListItem> = (query.data ?? []).map(
    (documentModel) => ({
      preferred: model.preferredDischargeBinaryIds.includes(documentModel.binaryId as never),
      document: documentModel,
    }),
  );

  return <EncounterRelatedDocumentsList isLoading={query.isLoading} items={items} />;
};

type PatientHeaderProps = {
  patient: PatientModel;
  patientUPID: string;
};

function PatientHeader({ patient, patientUPID }: PatientHeaderProps) {
  const { display, gender } = patient;

  return (
    <div className={tw`flex justify-between`}>
      {display}
      {gender ? ` (${gender[0].toUpperCase()})` : ''}
      {
        <Link
          href={`/patients/${patientUPID}`}
          className={tw`flex cursor-pointer items-center space-x-2 border-0 bg-transparent p-0 text-primary-text leading-none outline-none hover:text-primary-main`}
          target="_blank"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className={tw`h-3`} />
          <span className={tw`normal-case`}>Go To Profile</span>
        </Link>
      }
    </div>
  );
}

type DiagnosisDetailsProps = {
  diagnosis: Array<string>;
};
function DiagnosisDetails({ diagnosis }: DiagnosisDetailsProps) {
  if (diagnosis.length === 1) {
    return <span>{diagnosis[0]}</span>;
  }

  return (
    <ul className={tw`list-inside list-disc`}>
      {diagnosis.map((d) => (
        <li key={d}>{d}</li>
      ))}
    </ul>
  );
}
